/**
 * Where the verification link lands — §6.15.
 *
 * The endpoint existed and the email was written; there was nowhere for the
 * link to go. A verification email whose link opens nothing is a signup that
 * cannot be completed, and the person has no way to tell whether they did
 * something wrong.
 *
 * §6.15's promise is that signup answers identically whether or not an address
 * is known, and this page is where that promise is usually broken: a page
 * saying "unknown link" for a fresh address and "already verified" for a
 * registered one hands back exactly what signup refused to say, one click
 * later. So every outcome that is not success reads the same.
 *
 * Opening the page confirms nothing; the button does. Mail security scanners
 * open every link in a message before the person reads it, and where the
 * operator approves automatically, confirming the address approves the
 * account. A confirmation made on page load would be the scanner's, not the
 * person's.
 *
 * 0268 — when the login behind the link has no password a stranger could not
 * have chosen (signup keeps none; an older one set before the address was ever
 * proven counts as none), the button's answer asks for one instead of
 * confirming. The person types the password they chose when applying, or a
 * new one, and that confirms, sets it, and signs them in.
 */

import { useState } from 'react';

import {
  Callout, Field, Panel, PasswordInput, PASSWORD_MIN_LENGTH, explainFailure, kitText,
} from '@affdice/portal-kit';

import type { AffiliateApi } from './client.ts';
import { t } from './messages.ts';

type Outcome =
  | { readonly state: 'waiting' }
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly failure?: unknown }
  // The link is good and the login behind it gets its password here: a
  // password chosen when applying is not kept until the address is proven, so
  // nobody can register somebody else's address with a password of their own.
  | { readonly state: 'password'; readonly message: string }
  | {
      readonly state: 'ready';
      readonly value: { verified: boolean; message: string; signed_in?: boolean };
    };

export function VerifyPage({ api, token }: { api: AffiliateApi; token: string }) {
  const [done, setDone] = useState<Outcome>({ state: 'waiting' });
  // A link lasts 24 hours. Asking for another answers the same whatever the
  // link was, so what is shown is the server's sentence and nothing more.
  const [resend, setResend] = useState<
    { readonly state: 'idle' | 'loading' | 'error' } | { readonly state: 'done'; readonly message: string }
  >({ state: 'idle' });

  const [password, setPassword] = useState('');
  const [asked, setAsked] = useState<string | null>(null);

  const confirm = () => {
    setDone({ state: 'loading' });
    api.verify(token, asked === null ? undefined : password).then(
      (value) => {
        if (value.password_required) {
          setAsked(value.message);
          setDone({ state: 'password', message: value.message });
        } else {
          setDone({ state: 'ready', value });
        }
      },
      (failure: unknown) => setDone({ state: 'error', failure }),
    );
  };

  if (asked !== null && done.state !== 'ready') {
    const busy = done.state === 'loading';
    return (
      <div className="main signin">
        <div className="page-head">
          <h1>{t('verify.title')}</h1>
        </div>
        <Panel title={t('verify.password.title')}>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              if (!busy && password.length >= PASSWORD_MIN_LENGTH) confirm();
            }}
          >
            {done.state === 'error' ? (
              <Callout tone="error">
                {done.failure === undefined ? kitText('signin.unreachable') : explainFailure(done.failure).message}
              </Callout>
            ) : null}
            <p>{asked}</p>
            <Field label={kitText('password.title')} hint={t('verify.password.hint')}>
              <PasswordInput
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
              />
            </Field>
            <div>
              <button
                className="primary"
                type="submit"
                disabled={busy || password.length < PASSWORD_MIN_LENGTH}
                aria-busy={busy}
              >
                {busy ? t('verify.confirming') : t('verify.password.button')}
              </button>
            </div>
          </form>
        </Panel>
      </div>
    );
  }

  if (done.state !== 'ready') {
    const busy = done.state === 'loading';
    return (
      <div className="main signin">
        <div className="page-head">
          <h1>{t('verify.title')}</h1>
        </div>
        <Panel title={t('verify.whatToDo')}>
          <div className="stack">
            {done.state === 'error' ? (
              <Callout tone="error">{kitText('signin.unreachable')}</Callout>
            ) : null}
            <p>{t('verify.explain')}</p>
            <div>
              <button className="primary" type="button" disabled={busy} aria-busy={busy} onClick={confirm}>
                {busy ? t('verify.confirming') : t('verify.button')}
              </button>
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  const verified = done.value.verified;

  return (
    <div className="main signin">
      <div className="page-head">
        <h1>{verified ? t('verify.confirmed') : t('verify.invalid')}</h1>
      </div>

      <Panel title={verified ? t('verify.next') : t('verify.whatToDo')}>
        {/* §6.5 — confirming an address is not approval unless the operator
            approves automatically, and only the server knows which. Somebody
            who guesses wrong will ask why their links do not work, and the
            answer belongs here rather than in that conversation. */}
        <Callout>
          {done.value.message}
        </Callout>
        {/* Confirming with a password signs the person in: the session
            cookie came with this answer, so the portal opens on their
            account. */}
        {verified && done.value.signed_in ? (
          <div>
            <button className="primary" type="button" onClick={() => location.assign('/')}>
              {t('verify.continue')}
            </button>
          </div>
        ) : null}
        {!verified ? (
          <div className="stack">
            {resend.state === 'done' ? <Callout>{resend.message}</Callout> : null}
            {resend.state === 'error' ? <Callout tone="error">{kitText('signin.unreachable')}</Callout> : null}
            {resend.state !== 'done' ? (
              <div>
                <button
                  type="button"
                  disabled={resend.state === 'loading'}
                  aria-busy={resend.state === 'loading'}
                  onClick={() => {
                    setResend({ state: 'loading' });
                    api.resendVerification(token).then(
                      (r) => setResend({ state: 'done', message: r.message }),
                      () => setResend({ state: 'error' }),
                    );
                  }}
                >
                  {resend.state === 'loading' ? t('verify.resending') : t('verify.resend')}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Panel>
    </div>
  );
}
